import React, { useState } from "react";
import {
  useAddReviewMutation,
  useUpdateReviewMutation,
  useCheckHasPurchasedQuery,
} from "../../slices/reviewSlice";
import Button from "../utilComponents/Button";
import Modal from "../layout/Modal";
import Success from "../message/Success";
import Error from "../message/Error";
import Input from "../utilComponents/Input";
import TextArea from "../utilComponents/TextArea";
import { notEmpty } from "../../utils/validation";
import fullGrayStar from "../../assets/images/fullGrayStar.svg";
import fullYellowStar from "../../assets/images/fullYellowStar.svg";

const StarRating = ({ rating, setRating }) => {
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleMouseEnter = (index) => {
    setHoveredRating(index);
  };

  const handleMouseLeave = () => {
    setHoveredRating(0);
  };

  const handleClick = (index) => {
    setRating(index);
  };

  return (
    <div className="flex items-center mb-4">
      {[1, 2, 3, 4, 5].map((star) => (
        <img
          key={star}
          src={
            star <= (hoveredRating || rating) ? fullYellowStar : fullGrayStar
          }
          alt={`${star} star`}
          className="w-5 h-5 cursor-pointer" // Adjust width and height as needed
          onMouseEnter={() => handleMouseEnter(star)}
          onMouseLeave={handleMouseLeave}
          onClick={() => handleClick(star)}
        />
      ))}
    </div>
  );
};

const Review = ({ productId, reviews = [], existingReview }) => {
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [title, setTitle] = useState(existingReview?.title || "");
  const [comment, setComment] = useState(existingReview?.comment || "");
  const [showModal, setShowModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});

  const [addReview, { isLoading: isAdding }] = useAddReviewMutation();
  const [updateReview, { isLoading: isUpdating }] = useUpdateReviewMutation();

  const { data: purchaseStatus, isLoading: isCheckingPurchase } =
    useCheckHasPurchasedQuery(productId);

  const handleSubmit = async () => {
    const newErrors = {};

    if (!rating) newErrors.rating = "Rating is required.";
    if (!notEmpty(title)) newErrors.title = "Title is required.";
    if (!notEmpty(comment)) newErrors.comment = "Comment is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const reviewData = { rating, title, comment };

    try {
      if (existingReview) {
        await updateReview({ productId, reviewData }).unwrap();
        setSuccessMessage("Review updated successfully");
        setErrorMessage("");
      } else {
        await addReview({ productId, reviewData }).unwrap();
        setSuccessMessage("Review added successfully");
        setErrorMessage("");
      }
      setShowModal(false);
      setErrors({});
    } catch (error) {
      setErrorMessage(error.data?.message || "Failed to submit review");
      setSuccessMessage("");
    }
  };

  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum, review) => sum + review.rating, 0) /
          reviews.length
        ).toFixed(1)
      : null;

  return (
    <div className="w-full">
      {/* Show ratings summary only if reviews exist */}
      {reviews.length > 0 && (
        <div className="flex flex-wrap items-center justify-between mb-4">
          <div className="bg-agGrayLight p-4 shadow-sm rounded-lg w-full md:w-1/3">
            <h3 className="text-md font-semibold text-agBlack font-nunito">
              Ratings from verified purchases ({reviews.length})
            </h3>
            <div className="text-2xl font-bold text-agYellow my-2 font-nunito">
              {averageRating}/5
            </div>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <img
                  key={star}
                  src={
                    star <= Math.round(averageRating)
                      ? fullYellowStar
                      : fullGrayStar
                  }
                  alt={`${star} star`}
                  className="w-5 h-5" // Adjust width and height as needed
                />
              ))}
            </div>
          </div>
          {!isCheckingPurchase && purchaseStatus?.hasPurchased && (
            <div className="mt-4 md:mt-0">
              <button
                onClick={() => setShowModal(true)}
                className="text-sm text-agBlue hover:underline"
              >
                Write review
              </button>
            </div>
          )}
        </div>
      )}

      {/* Show "No reviews yet" and optionally "Write Review" */}
      {reviews.length === 0 && (
        <div className="flex justify-between items-center bg-agGrayLight p-4 rounded-lg mb-4">
          <p className="text-sm text-agLightGray">No reviews yet.</p>
          {!isCheckingPurchase && purchaseStatus?.hasPurchased && (
            <button
              onClick={() => setShowModal(true)}
              className="text-sm text-agBlue hover:underline"
            >
              Write review
            </button>
          )}
        </div>
      )}

      {/* Existing Reviews */}
      {reviews.length > 0 && (
        <div>
          {reviews.map((review, index) => (
            <div key={index} className="mb-4 md:mb-8">
              <div className="flex items-center mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <img
                    key={star}
                    src={star <= review.rating ? fullYellowStar : fullGrayStar}
                    alt={`${star} star`}
                    className="w-5 h-5" // Adjust width and height as needed
                  />
                ))}
              </div>
              <h4 className="text-sm font-semibold font-nunito text-agBlack">
                {review.title}
              </h4>
              <p className="text-sm font-normal font-nunito text-agLightGray mb-2">
                {review.comment}
              </p>
              {/* <p className="text-sm font-normal font-nunito text-agLightGray mb-2">
                {review.vendorReply}
              </p> */}
              <p className="text-xs text-agLightGray">
                by{" "}
                <span className="text-agBlack font-semibold font-nunito">
                  {review.firstName || "Anonymous"}
                </span>{" "}
                on{" "}
                {new Date(review.createdAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
              {review.vendorReply && (
                <div className="bg-agBg rounded-lg p-3 mt-2">
                  <p className="text-xs font-medium text-agBlack mb-1">
                    Vendor's comment:
                  </p>
                  <p className="text-xs text-agLightGray">
                    {review.vendorReply}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal for Writing/Updating Review */}
      <Modal isVisible={showModal} onClose={() => setShowModal(false)}>
        <div className="w-full p-4">
          <h3 className="text-md font-semibold font-nunito text-agBlack mb-2">
            {existingReview ? "Update Your Review" : "Write a Review"}
          </h3>
          {successMessage && (
            <Success
              message={successMessage}
              onClose={() => setSuccessMessage("")}
            />
          )}
          {errorMessage && (
            <Error message={errorMessage} onClose={() => setErrorMessage("")} />
          )}

          <StarRating rating={rating} setRating={setRating} />
          {errors.rating && <Error message={errors.rating} />}

          <Input
            type="text"
            placeholder="Enter the review title"
            value={title}
            isFullWidth
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) setErrors((prev) => ({ ...prev, title: null }));
            }}
          />
          {errors.title && <Error message={errors.title} />}

          <TextArea
            placeholder="Write your review here"
            rows={6}
            value={comment}
            isFullWidth
            onChange={(e) => {
              setComment(e.target.value);
              if (errors.comment)
                setErrors((prev) => ({ ...prev, comment: null }));
            }}
          />
          {errors.comment && <Error message={errors.comment} />}

          <Button
            fullWidth
            onClick={handleSubmit}
            isLoading={isAdding || isUpdating}
          >
            Submit
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Review;
